// =====================================================================================
// FILE: src/constants/phases.js
// =====================================================================================

/**
 * Phase definitions for Game Timeline mode.
 * Categories are grouped under phases, displayed as visual separators.
 * 
 * Each phase has:
 * - label: Display name for the phase header
 * - categories: Array of category names that belong to this phase
 * 
 * To add/remove/modify phases or categories:
 * 1. Edit this file
 * 2. No code changes needed - UI will automatically reflect changes
 * 
 * IMPORTANT: Categories must match exactly with Airtable category names
 */

export const GAME_TIMELINE_PHASES = [
  {
    label: "Venue Entitlements",
    categories: [
      "Disney",
      "Disney Entitlement",
      "AFL Marketing",
      "MCG Venue Entitlements",
      "Venue Operations Pre-Match",
      "Venue Operations Post-Match",
    ],
  },
  {
    label: "Ancillary Content",
    categories: [
      "Ponsford Stage",
      "DJ Booth",
      "Goal Celebration",
      "In-Game Triggers Interchange",
      "Interchange",
      "In-Game Triggers",
      "Robbie Flowers",
    ],
  },
  {
    label: "Pre-Game",
    categories: [
      "Gates-30 Loop",
      "Gates-30 Moments",
      "30-Bounce Loop",
      "30-Bounce Moments",
    ],
  },
  {
    label: "In-Game (Quarters)",
    categories: [
      "Q1 LED Loop",
      "Q1 Moments",
      "Q2 LED Loop",
      "Q2 Moments",
      "Q3 LED Loop",
      "Q3 Moments",
      "Q4 LED Loop",
      "Q4 Moments",
    ],
  },
  {
    label: "Quarter Breaks",
    categories: [
      "QT Loop",
      "QT Moments",
      "HT Loop",
      "HT Moments",
      "3QT Loop",
      "3QT Moments",
    ],
  },
  {
    label: "Post-Game",
    categories: [
      "PG WIN Loop",
      "PG WIN Moments",
      "PG LOSE Moments",
      "PG LOSE Loop",
    ],
  },
];

/**
 * Helper function to get phase for a given category
 * @param {string} category - Category name
 * @returns {string|null} - Phase label or null if not found
 */
export function getPhaseForCategory(category) {
  for (const phase of GAME_TIMELINE_PHASES) {
    if (phase.categories.includes(category)) {
      return phase.label;
    }
  }
  return null;
}

/**
 * Helper function to group categories by phase
 * @param {string[]} categories - Array of category names
 * @returns {Array<{phase: string, categories: string[]}>} - Categories grouped by phase
 */
export function groupCategoriesByPhase(categories) {
  const grouped = [];
  
  // Handle categories that don't belong to any phase (uncategorized) - put at TOP
  const allPhasedCategories = new Set(
    GAME_TIMELINE_PHASES.flatMap(p => p.categories)
  );
  const uncategorized = categories.filter(cat => !allPhasedCategories.has(cat));
  
  if (uncategorized.length > 0) {
    grouped.push({
      phase: "Other",
      categories: uncategorized,
    });
  }
  
  // Then add all the defined phases in order
  for (const phase of GAME_TIMELINE_PHASES) {
    // Find which categories from the data belong to this phase
    const matchingCategories = categories.filter(cat => 
      phase.categories.includes(cat)
    );
    
    if (matchingCategories.length > 0) {
      grouped.push({
        phase: phase.label,
        categories: matchingCategories,
      });
    }
  }
  
  return grouped;
}

/**
 * Helper to check if a mode should use phasing
 * Currently only Game Timeline (Out of Game) uses phases
 * @param {boolean} isInGame 
 * @param {boolean} isLed 
 * @param {boolean} isTeam 
 * @param {boolean} isIptv 
 * @returns {boolean}
 */
export function shouldUsePhasing(isInGame, isLed, isTeam, isIptv) {
  // Only Game Timeline mode (out of game) uses phasing
  return !isInGame && !isLed && !isTeam && !isIptv;
}
